import {useCallback} from 'react'
import {DragDropContext, DropResult} from 'react-beautiful-dnd'
import {Spinner} from 'react-bootstrap'
import {IKanbanStage, IKanbanTask} from '../../../types/kanban'
import {KanbanColumn} from './KanbanColumn'

interface MovePayload {
  taskId: string
  fromStageId: string
  toStageId: string
  fromIndex: number
  toIndex: number
}

interface Props {
  stages: IKanbanStage[]
  columns: Record<string, IKanbanTask[]>
  loading: boolean
  onTaskMove: (payload: MovePayload) => void
  onAddTask?: (stage: IKanbanStage) => void
  canCreateTask?: boolean
  onOpenAttachments?: (task: IKanbanTask) => void
  onEditStage?: (stage: IKanbanStage) => void
  onDeleteStage?: (stage: IKanbanStage) => void
  onEditTask?: (task: IKanbanTask) => void
  onAddComment?: (task: IKanbanTask) => void
  currentUserEmail?: string
  onArchiveTask?: (task: IKanbanTask) => void
  onDeleteTask?: (task: IKanbanTask) => void
  onAddStage?: () => void
  onQuickAddTask?: (stage: IKanbanStage, title: string) => Promise<void> | void
  quickAddStageId?: string | null
  onOpenReorderModal?: () => void
}

export const KanbanBoard: React.FC<Props> = ({
  stages,
  columns,
  loading,
  onTaskMove,
  onAddTask,
  canCreateTask,
  onOpenAttachments,
  onEditStage,
  onDeleteStage,
  onEditTask,
  onAddComment,
  currentUserEmail,
  onArchiveTask,
  onDeleteTask,
  onAddStage,
  onQuickAddTask,
  quickAddStageId,
  onOpenReorderModal,
}) => {
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const {destination, source, draggableId, type} = result
      if (!destination) return
      if (destination.droppableId === source.droppableId && destination.index === source.index)
        return
      onTaskMove({
        taskId: draggableId,
        fromStageId: source.droppableId,
        toStageId: destination.droppableId,
        fromIndex: source.index,
        toIndex: destination.index,
      })
    },
    [onTaskMove]
  )

  return (
    <div className='kanban-board'>
      {loading && (
        <div className='kanban-board__loading'>
          <Spinner animation='border' size='sm' /> <span>Loading board...</span>
        </div>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className='kanban-board__columns-wrapper'>
          <div className='kanban-board__columns'>
            {stages.map((stage) => (
              <div className='kanban-column__draggable' key={stage.id}>
                <KanbanColumn
                  stage={stage}
                  tasks={columns[stage.id] ?? []}
                  onAddTask={onAddTask}
                  canCreateTask={!!canCreateTask}
                  onOpenAttachments={onOpenAttachments}
                  onEditStage={onEditStage}
                  onDeleteStage={onDeleteStage}
                  onEditTask={onEditTask}
                  onAddComment={onAddComment}
                  currentUserEmail={currentUserEmail}
                  onArchiveTask={onArchiveTask}
                  onDeleteTask={onDeleteTask}
                  onQuickAddTask={onQuickAddTask}
                  quickAddLoading={quickAddStageId === stage.id}
                  onOpenReorderModal={onOpenReorderModal}
                />
              </div>
            ))}
            {onAddStage && (
              <div className='kanban-column kanban-column__add-placeholder'>
                <button type='button' onClick={onAddStage}>
                  <i className='bi bi-plus-circle me-2' />
                  New Column
                </button>
              </div>
            )}
          </div>
        </div>
      </DragDropContext>
    </div>
  )
}
